import { Link } from "@tanstack/react-router";
import type { PolicyClaimCounts } from "@/hooks/use-policy";
import { formatCurrency, formatDate } from "@/lib/format";

interface Props {
  policyNumber?: string;
  insuredName: string | null;
  insuredAddress: string | null;
  inceptionDate: Date | string | null;
  expirationDate: Date | string | null;
  claimCounts?: PolicyClaimCounts;
  actions?: React.ReactNode;
}

function ClaimCountLink({ count, search }: { count: number; search: Record<string, string> }) {
  if (count === 0) return <span>{count}</span>;
  return (
    <Link to="/claims/mrp" search={search} className="text-primary hover:underline">
      {count}
    </Link>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 break-words font-medium text-sm">{value ?? "—"}</div>
    </div>
  );
}

export function PolicySummaryCard({
  policyNumber,
  insuredName,
  insuredAddress,
  inceptionDate,
  expirationDate,
  claimCounts,
  actions,
}: Props) {
  const baseSearch = policyNumber ? { policyNumber } : {};

  return (
    <>
      {actions ? <div className="mb-3 flex flex-wrap gap-1.5">{actions}</div> : null}

      <h2 className="font-bold text-xl">{insuredName ?? "—"}</h2>
      <div className="text-muted-foreground text-sm">
        {insuredAddress ?? "No mailing address on file"}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <Field label="Inception Date" value={formatDate(inceptionDate)} />
        <Field label="Expiration Date" value={formatDate(expirationDate)} />
      </div>

      {claimCounts && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-xs">
                <th className="pb-1 text-left font-medium" />
                <th className="pb-1 text-right font-medium">Total</th>
                <th
                  className="pb-1 text-right font-medium"
                  title="Open Indemnity Count — open claims that pay wage replacement to an injured worker who can't work."
                >
                  <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
                    Open IN
                  </span>
                </th>
                <th
                  className="pb-1 text-right font-medium"
                  title="Open Medical Only Count — open claims paying only for medical treatment; the worker hasn't missed work beyond the waiting period."
                >
                  <span className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2">
                    Open MO
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="py-1.5 text-muted-foreground">Claims</td>
                <td className="py-1.5 text-right font-medium">
                  <ClaimCountLink count={claimCounts.total} search={baseSearch} />
                </td>
                <td className="py-1.5 text-right font-medium">
                  <ClaimCountLink
                    count={claimCounts.openIndemnity}
                    search={
                      policyNumber
                        ? { policyNumber, isOpenIndemnity: "true" }
                        : { isOpenIndemnity: "true" }
                    }
                  />
                </td>
                <td className="py-1.5 text-right font-medium">
                  <ClaimCountLink
                    count={claimCounts.openMedicalOnly}
                    search={
                      policyNumber
                        ? { policyNumber, isOpenMedicalOnly: "true" }
                        : { isOpenMedicalOnly: "true" }
                    }
                  />
                </td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 text-muted-foreground">Reserve</td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.reserve.total)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.reserve.openIndemnity)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.reserve.openMedicalOnly)}
                </td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 text-muted-foreground">Paid</td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.paid.total)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.paid.openIndemnity)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.paid.openMedicalOnly)}
                </td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 text-muted-foreground">Recovered</td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.recovered.total)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.recovered.openIndemnity)}
                </td>
                <td className="py-1.5 text-right font-medium tabular-nums">
                  {formatCurrency(claimCounts.financials.recovered.openMedicalOnly)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
